import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import AdTable from './AdsTable.js';
import { useTableState } from '../utils/useTableState.js';
import { columnsConfig } from '../MyAds/tableConfig.js';
import FilterBar from './FilterBar';
import { usePersistedFilterState } from '../utils/usePersistedFilterState.js';
const types = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "full_page",
    label: "Full Image",
  },
  {
    value: "full_video",
    label: "Full Video",
  },
  {
      value:"half_video",
      label:"Half Video"
  },
  {
    value: "promoted_image_story",
    label: "Promoted Image Story ",
  },
  {
    value:"promoted_video_story",
    label:"Promoted Video Story"
  },
  {
    vaule: "classifiedads",
    label: "Classified Ads"
  }
]
// Status options from cellRenderers.js
const STATUS_OPTIONS = [
    { value: 2, label: 'Saved' },
    { value: 3, label: 'Picked' },
    { value: 4, label: 'Completed' },
    { value: 5, label: 'Rejected' },
    { value: 6, label: 'Accepted' },
];

const CompletedAds = ({ isActive }) => {
    const {
        fetchCompletedAds,
        currentPage,
        setCurrentPage,
        setAdsData,
        adsData,
        hasMore,
        designers,
        contentWriters,
        fetchDesigners,
        fetchContentWriters,
        loading,
        showActionsMenu,
        modal,
        drawer,
        expandedVersionsRow,
        actionHandlers,
        renderCell,
        getModalAlt,
        closeExpandedRows,
        handleDesignerChange,
        handleContentWriterChange,
        fetchContent,
        salesList,
        getSalesList,
        fetchSalesLocations,
        salesLocations,
        totalRecords
    } = useTableState();

    // Filter state
    // const today = new Date().toISOString().split('T')[0];
    // const tomorrow = new Date();
    // tomorrow.setDate(tomorrow.getDate() + 1);
    // const tomorrowStr = tomorrow.toISOString().split('T')[0];
    const [dateRange, setDateRange] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.dateRange', { startDate: null, endDate: null });
    const [designer, setDesigner] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.designer', null);
    const [contentWriter, setContentWriter] = useState(null);
    const [status, setStatus] = useState(null);
    const [adType, setAdType] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.adType', null);
    const [search, setSearch] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.search', '');
    const [salesPerson, setSalesPerson] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.salesPerson', null);
    const [salesLocation, setSalesLocation] = usePersistedFilterState('alpha_designer.my_ads.completed_ads.salesLocation', []);

    // Designer options for react-select
    const designerOptions = useMemo(() =>
        designers.map(d => ({ value: d.designer_id, label: d.name })),
        [designers]
    );

    // Fetch designers and content writers on mount
    useEffect(() => {
        fetchDesigners();
        fetchContentWriters();
        getSalesList();
        fetchSalesLocations();
    }, []);

    const loadMoreCtx = useRef({});
    loadMoreCtx.current = { loading, hasMore, currentPage, isActive, dateRange, designer, contentWriter, status, adType, search, salesPerson, salesLocation };

    // Reset and fetch on filter/tab change
    useEffect(() => {
        setCurrentPage(0);
        setAdsData([]);
        fetchCompletedAds(0, isActive, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            designerId: designer ? designer.value : null,
            contentWriterId: contentWriter ? contentWriter.value : null,
            status: status ? status.value : null,
            adType: adType && adType.value !== 'all' ? adType.value : null,
            search: search || null,
            sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
            sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
        }, false);
    }, [isActive, dateRange, designer, contentWriter, status, adType, search, salesPerson, salesLocation]);

    // On-demand re-fetch of the current filters/page, separate from the auto
    // fetch on filter/tab change or pagination.
    const handleRefresh = useCallback(() => {
        setCurrentPage(0);
        setAdsData([]);
        fetchCompletedAds(0, isActive, {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            designerId: designer ? designer.value : null,
            contentWriterId: contentWriter ? contentWriter.value : null,
            status: status ? status.value : null,
            adType: adType && adType.value !== 'all' ? adType.value : null,
            search: search || null,
            sales_person_id: salesPerson?.value ? [salesPerson.value] : null,
            sales_location: salesLocation?.length ? salesLocation.map((o) => o.value) : null,
        }, false);
    }, [fetchCompletedAds, isActive, dateRange, designer, contentWriter, status, adType, search, salesPerson, salesLocation]);

    const handleLoadMore = useCallback(() => {
        const ctx = loadMoreCtx.current;
        if (ctx.loading || !ctx.hasMore) return;
        const nextPage = ctx.currentPage + 1;
        setCurrentPage(nextPage);
        fetchCompletedAds(nextPage, ctx.isActive, {
            startDate: ctx.dateRange.startDate,
            endDate: ctx.dateRange.endDate,
            designerId: ctx.designer ? ctx.designer.value : null,
            contentWriterId: ctx.contentWriter ? ctx.contentWriter.value : null,
            status: ctx.status ? ctx.status.value : null,
            adType: ctx.adType && ctx.adType.value !== 'all' ? ctx.adType.value : null,
            search: ctx.search || null,
            sales_person_id: ctx.salesPerson?.value ? [ctx.salesPerson.value] : null,
            sales_location: ctx.salesLocation?.length ? ctx.salesLocation.map((o) => o.value) : null,
        }, true);
    }, [fetchCompletedAds]);

    // Handlers
    const handleDateChange = (val) => setDateRange(val);
    const handleDesignerChangeSelect = (val) => setDesigner(val);
    const handleContentWriterChangeSelect = (val) => setContentWriter(val);
    const handleStatusChange = (val) => setStatus(val);
    const handleAdTypeChange = (val) => setAdType(val);
    const handleSearchChange = (val) => setSearch(val);
    const handleSalesPersonChange = (val) => setSalesPerson(val);
    const handleSalesLocationChange = (val) => setSalesLocation(val);

    return (
        <div>
            <FilterBar
                dateRange={dateRange}
                onDateChange={handleDateChange}
                designer={designer}
                onDesignerChange={handleDesignerChangeSelect}
                designerOptions={designerOptions}
                status={status}
                onStatusChange={handleStatusChange}
                statusOptions={STATUS_OPTIONS}
                showDesigner={true}
                showStatus={false}
                adType={adType}
                onAdTypeChange={handleAdTypeChange}
                adTypeOptions={types}
                showAdType={true}
                search={search}
                onSearchChange={handleSearchChange}
                showSearch={true}
                salesPerson={salesPerson}
                onSalesPersonChange={handleSalesPersonChange}
                salesPersonOptions={salesList}
                showSalesPerson={true}
                salesLocation={salesLocation}
                onSalesLocationChange={handleSalesLocationChange}
                showSalesLocation={true}
                salesLocationOptions={salesLocations}
            />
           <div className='flex flex-col items-start justify-center gap-2'>
                <div className="px-2 py-1 shadow-md rounded-lg bg-gray-300">
                    <span>Total Ads: {totalRecords}</span>
                </div>
              <AdTable
                data={adsData}
                columns={columnsConfig.completed_ads}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                designers={designers}
                showActionsMenu={showActionsMenu}
                modal={modal}
                drawer={drawer}
                expandedVersionsRow={expandedVersionsRow}
                actionHandlers={actionHandlers}
                renderCell={renderCell}
                getModalAlt={getModalAlt}
                closeExpandedRows={closeExpandedRows}
                handleDesignerChange={(creativeId, designerId, designerName, creativeName) => handleDesignerChange(creativeId, designerId, designerName, creativeName, handleRefresh)}
                fetchContent={fetchContent}
              />
            </div>
        </div>
    );
};

export default CompletedAds; 